import { Drawer, Empty } from 'antd';
import { useQuery } from '@tanstack/react-query';
import type { DutyHistoryEntryDto, Paginated } from '@pams/shared';
import { OfficerAvatar } from '@/components/OfficerAvatar';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { FILL_COPY } from './copy';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  WHO CHANGED WHO ON THIS DUTY. [PRE-P6.2]
 *
 *  Every name, swap, removal and completion has been in `audit_logs` since [P4], keyed on
 *  `(DEPLOYMENT, deploymentId)` — and no client had ever read one, because `GET /audit` is
 *  SUPER_ADMIN only and the people who do the swapping are divisional nodals. A trail its own
 *  actors cannot see settles nothing until it has already become an argument.
 *
 *  ⚠️ THE WHOLE FILL HISTORY, NOT ONLY SWAPS. "R. Das replaced A. Sen" means nothing without
 *  the line above it saying A. Sen was named there in the first place.
 *
 *  ⚠️ A `Drawer`, AND IT IS EXEMPTED FROM THE OVERLAY CENSUS BY NAME. `census.spec.ts`
 *  requires every `Drawer` in `web/src` to be listed in `e2e/fixtures/overlays.ts` or
 *  exempted with a reason; the sweep's routes are static strings and this one lives at
 *  `/duties/:dutyId`, which needs a fixture id. It joins `OfficerSearchSheet` and `SlotChip`
 *  in EXEMPT, swept by `specs/journeyFill.spec.ts` for the same reason they are.
 *
 *  ⚠️ ITS QUERY IS OUTSIDE `queryKeys.fill`, DELIBERATELY. `lib/fillKeyIsolation.test.ts`
 *  holds that nothing may invalidate at or above a live sheet, and a history that refetched
 *  on every write would be doing exactly that from the other direction. It is read when the
 *  drawer OPENS — `enabled: open` — and is stale the moment it closes, which is correct for
 *  a log nobody edits.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export type DutyHistoryDrawerProps = {
  dutyId: string;
  open: boolean;
  onClose: () => void;
};

/** Bounded, and it says so when it truncates. A duty's history is dozens of rows, not pages. */
const HISTORY_LIMIT = 100;

const ACTION_LABEL: Record<DutyHistoryEntryDto['action'], string> = {
  ASSIGNMENT_NAMED: 'Named',
  ASSIGNMENT_SWAPPED: 'Swapped',
  ASSIGNMENT_REMOVED: 'Removed',
  ALLOCATION_COMPLETED: 'Marked complete',
};

/** `2:41 pm, 21 Aug` — the app is 12-hour throughout ([P4-UX.2]). */
const when = (iso: string): string =>
  new Date(iso).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

export const DutyHistoryDrawer = ({ dutyId, open, onClose }: DutyHistoryDrawerProps) => {
  const history = useQuery({
    queryKey: queryKeys.fill.history(dutyId),
    queryFn: ({ signal }) =>
      api.get<Paginated<DutyHistoryEntryDto>>(`/duties/${dutyId}/history`, {
        query: { page: 1, limit: HISTORY_LIMIT, sortOrder: 'desc' },
        signal,
      }),
    enabled: open && dutyId !== '',
  });

  const rows = history.data?.items ?? [];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={FILL_COPY.historyTitle}
      placement="right"
      width={420}
      rootClassName="duty-history-drawer"
    >
      <div data-testid="duty-history">
        {history.isPending ? (
          <p className="text-sm text-muted-foreground">{FILL_COPY.historyLoading}</p>
        ) : rows.length === 0 ? (
          <Empty description={FILL_COPY.historyEmpty} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <ol className="flex flex-col gap-3">
            {rows.map((row) => (
              <li key={row.id} className="border-b border-border pb-3 last:border-0" data-testid="duty-history-row">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-sm font-medium">{ACTION_LABEL[row.action]}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">{when(row.at)}</span>
                </div>

                {/* ⚠️ BOTH SIDES, because a swap is only legible as a pair. `before` is absent
                    on a naming and `after` on a removal, and neither is an error. */}
                <p className="mt-1 flex flex-wrap items-center gap-1.5 text-sm">
                  {row.before ? (
                    <span className="flex items-center gap-1.5 text-muted-foreground line-through">
                      <OfficerAvatar name={row.before.name} className="size-5" />
                      {row.before.name}
                    </span>
                  ) : null}
                  {row.before && row.after ? <span aria-hidden>→</span> : null}
                  {row.after ? (
                    <span className="flex items-center gap-1.5">
                      <OfficerAvatar name={row.after.name} className="size-5" />
                      {row.after.name}
                    </span>
                  ) : null}
                </p>

                {row.reason ? <p className="mt-1 text-xs text-muted-foreground">“{row.reason}”</p> : null}

                {/* ⚠️ WHO DID IT is the point of an audit trail, so it is never omitted. */}
                <p className="mt-1 text-xs text-muted-foreground">{FILL_COPY.historyBy(row.actor?.name ?? '—')}</p>
              </li>
            ))}
          </ol>
        )}

        {history.data && history.data.total > rows.length ? (
          <p className="mt-3 text-xs text-muted-foreground">
            {FILL_COPY.historyTruncated(rows.length, history.data.total)}
          </p>
        ) : null}
      </div>
    </Drawer>
  );
};
